import { useState, useEffect } from "react";
import Select from 'react-select';
import swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import RightSideDetails from "../global/rightSideDetails";
import { getMembershipList, getSubscriptionValidityForAdmin } from "../api/paymentAPI";
import UpdateMembershipModal from "./updateMembershipModal";
import subscription_expired from '../../assets/images/subscription_expired.png';

const MembershipManagement = (props) => {

    const [membershipList,       setMembershipList]       = useState([]);
    const [rightSideDetails,     setRightSideDetails]     = useState(false);
    const [navState,             setNavState]             = useState('Golf Users');
    const [isInit,               setIsInit]               = useState(true);
    const [refresh,              setRefresh]              = useState(false);
    const [selectedMember,       setSelectedMember]       = useState(null);
    const [membershipValidity,   setMembershipValidity]   = useState([]);
    const [membershipRecord,     setMembershipRecord]     = useState({});
    const [showMembershipModal,  setShowMembershipModal]  = useState(false);
    const [subRefresh,           setSubRefresh]           = useState(false);
    const [subNavState,          setSubNavState]          = useState('Current Membership');
    const [statusFilter,         setStatusFilter]         = useState('All');
    const [dateRange,            setDateRange]            = useState('30');
    const [fromDate,             setFromDate]             = useState('');
    const [toDate,               setToDate]               = useState('');

    const tabTypeMap = {
        'Golf Users':   'Golf',
        'Social Users': 'Social',
    };

    const subStatusMap = {
        'Current Membership': 'Active',
        'Upcoming':             'Upcoming',
        'Expired':              'Expired',
    };

    useEffect(() => {
        props.title('Membership Validity', 'membership-management');
    }, []);

    useEffect(() => {
        if (rightSideDetails) {
            Config.Core.OpenDrawer();
        } else {
            setMembershipValidity([]);
            setMembershipRecord({});
            setSubNavState('Current Membership');
        }
    }, [rightSideDetails]);

    useEffect(() => {
        if (rightSideDetails && selectedMember) {
            fetchSubscriptionValidity();
        }
    }, [rightSideDetails, subRefresh]);

    useEffect(() => {
        if (!isInit) {
            fillDataTable();
        }
    }, [membershipList]);

    const dateRangeOptions = [
        { value: '30',     label: '30 Days' },
        { value: '60',     label: '60 Days' },
        { value: '90',     label: '90 Days' },
        { value: 'Custom', label: 'Custom'  },
    ];

    const getDateParams = () => {
        if (!dateRange) return { fd: null, td: null };
        if (dateRange === 'Custom') return { fd: fromDate || null, td: toDate || null };
        return {
            fd: moment().format('YYYY-MM-DD'),
            td: moment().add(parseInt(dateRange), 'days').format('YYYY-MM-DD'),
        };
    };

    useEffect(() => {
        if (!navState && !refresh) return;
        if (dateRange === 'Custom' && (!fromDate || !toDate)) return;
        fetchMembership();
    }, [navState, statusFilter, dateRange, fromDate, toDate, refresh]);

    useEffect(() => {
        if (dateRange !== 'Custom') return;

        $('#txtMemberFromDate').datepicker({ format: 'dd-mm-yyyy' });
        $('#txtMemberFromDate').off('change').on('change', function (e) {
            setFromDate(moment(e.target.value, 'DD-MM-YYYY').format('YYYY-MM-DD'));
        });

        $('#txtMemberToDate').datepicker({ format: 'dd-mm-yyyy' });
        $('#txtMemberToDate').off('change').on('change', function (e) {
            setToDate(moment(e.target.value, 'DD-MM-YYYY').format('YYYY-MM-DD'));
        });
    }, [dateRange]);

    const handleNavChange = (state) => {
        setStatusFilter('All');
        setNavState(state);
    };

    const fetchMembership = async () => {
        try {
            const { fd, td } = getDateParams();
            const res = await getMembershipList(tabTypeMap[navState], statusFilter, fd, td);
            isInit ? setIsInit(false) : $('#tblMembership').KTDatatable().destroy();
            setMembershipList(res);
        } catch (error) {
            swal.fire({ icon: 'error', titleText: error.message });
        }
    };

    const fetchSubscriptionValidity = async () => {
        try {
            const res = await getSubscriptionValidityForAdmin(selectedMember.GroupID);
            setMembershipValidity(res);
        } catch (err) {
            swal.fire({ icon: 'error', titleText: err.message });
        }
    };

    function calculateProgress(startDate, endDate) {
        const start = new Date(startDate);
        const end   = new Date(endDate);
        const now   = new Date();
        return `${Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100))}%`;
    }

    function exportSheet(type) {
        const filename = `${moment().format('DD-MM-YYYY')} - Membership Management - ${navState} - ${statusFilter}`;
        const headers = ['Member Name', 'Membership No', 'Mobile', 'Email', 'Membership Type', 'Membership Name', 'Membership Expires', 'Status'];
        const rows = membershipList.map(row => {
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const expiry = row.SubscriptionExpiresOn ? new Date(row.SubscriptionExpiresOn + 'T00:00:00') : null;
            const daysLeft = expiry ? Math.round((expiry - today) / 86400000) + 1 : null;
            const isLifetime = row.SubscriptionExpiresOn && row.SubscriptionExpiresOn.startsWith('9999');
            let status;
            if (isLifetime) {
                status = 'Lifetime';
            } else if (row.IsSubscriptionExpires !== 'Y') {
                status = 'Expired';
            } else if (daysLeft <= 7) {
                status = `${daysLeft} Day${daysLeft === 1 ? '' : 's'} Left`;
            } else if (daysLeft <= 15) {
                status = `${daysLeft} Days Left`;
            } else {
                status = 'Active';
            }
            return [
                `${row.FirstName} ${row.LastName}`,
                row.MembershipNo || '',
                row.Mobile || '',
                row.Email || '',
                row.MembershipTypeName || '',
                row.SubscriptionName || '',
                isLifetime ? 'Lifetime' : row.SubscriptionExpiresOn ? moment(row.SubscriptionExpiresOn).format('DD-MMM-YYYY') : '-',
                status,
            ];
        });

        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Membership');
        XLSX.writeFile(wb, `${filename}.${type === 'xlsx' ? 'xlsx' : 'csv'}`, type === 'csv' ? { bookType: 'csv' } : {});
    }

    function fillDataTable() {
        const dataTable = $('#tblMembership').KTDatatable({
            data: {
                saveState: false,
                source: membershipList,
                pageSize: 20,
            },
            layout: {
                scroll: false,
                footer: false,
            },
            sortable: true,
            pagination: true,
            search: {
                input: $('#generalSearch'),
            },
            columns: [
                {
                    field: 'FirstName',
                    title: 'Member',
                    width: 220,
                    autoHide: false,
                    template: function (row) {
                        return `<div class="kt-user-card-v2">
                                    <div class="kt-user-card-v2__pic">
                                        ${row.Photo && row.Photo !== ''
                                            ? `<img src="${row.Photo}" alt="user-profile" height="50" width="50">`
                                            : `<div class="kt-badge kt-badge--xl kt-badge--info">${row.FirstName.substring(0, 1)}</div>`}
                                    </div>
                                    <div class="kt-user-card-v2__details text-clip">
                                        <span class="kt-user-card-v2__name text-clip">${row.FirstName} ${row.LastName}</span>
                                        <span class="kt-user-card-v2__desc text-clip">${row.MembershipNo || ''}</span>
                                    </div>
                                </div>`;
                    },
                },
                {
                    field: 'Mobile',
                    title: 'Mobile',
                    width: 130,
                    autoHide: false,
                },
                {
                    field: 'Email',
                    title: 'Email',
                    width: 200,
                    autoHide: false,
                },
                {
                    field: 'MembershipTypeName',
                    title: 'Membership Type',
                    width: 160,
                    autoHide: false,
                    template: function (row) {
                        return `<span class="kt-badge kt-badge--${row.MembershipType == 1 ? 'success' : 'info'} kt-badge--pill padding-lr20 padding-tb10">${row.MembershipTypeName.split(' ')[0] || ''}</span>`;
                    },
                },
                {
                    field: 'SubscriptionName',
                    title: 'Membership Name',
                    width: 180,
                    autoHide: false,
                },
                {
                    field: 'SubscriptionExpiresOn',
                    title: 'Membership Expires',
                    width: 170,
                    autoHide: false,
                    template: function (row) {
                        if (row.SubscriptionExpiresOn && row.SubscriptionExpiresOn.startsWith('9999')) {
                            return '<span class="kt-badge kt-badge--warning kt-badge--inline kt-badge--pill padding-lr10">Lifetime</span>';
                        }
                        return row.SubscriptionExpiresOn
                            ? moment(row.SubscriptionExpiresOn).format('DD-MMM-YYYY')
                            : '-';
                    },
                },
                {
                    field: 'IsSubscriptionExpires',
                    title: 'Status',
                    width: 100,
                    autoHide: false,
                    template: function (row) {
                        if (row.SubscriptionExpiresOn && row.SubscriptionExpiresOn.startsWith('9999')) {
                            return '<span class="kt-badge kt-badge--warning kt-badge--pill" style="padding: 8px 20px;">Lifetime</span>';
                        }
                        if (row.IsSubscriptionExpires !== 'Y') {
                            return '<span class="kt-badge kt-badge--grey kt-badge--pill" style="padding: 8px 24px;">Expired</span>';
                        }
                        const today = new Date(); today.setHours(0, 0, 0, 0);
                        const expiry = new Date(row.SubscriptionExpiresOn + 'T00:00:00');
                        const daysLeft = Math.round((expiry - today) / 86400000) + 1;
                        if (daysLeft <= 7) {
                            return `<span class="kt-badge kt-badge--danger kt-badge--pill" style="padding: 8px 33px; color: #fff; white-space: nowrap;">${daysLeft} Day${daysLeft === 1 ? '' : 's'} Left</span>`;
                        }
                        if (daysLeft <= 15) {
                            return `<span class="kt-badge kt-badge--grey kt-badge--pill" style="padding: 8px 33px; color: #fd397a; white-space: nowrap;">${daysLeft} Days Left</span>`;
                        }
                        return '<span class="kt-badge kt-badge--success kt-badge--pill" style="padding: 8px 24px;">Active</span>';
                    },
                },
            ],
        });

        /**@description custom jQuery code to enable horizontal scrolling - keep scroll: false for this*/

        $('#root .kt-grid--hor').addClass('max-width100');
        $('#tblMembership .kt-datatable__table').addClass('overflow-x-auto');
        Config.Core.InitPopover();
        Config.Core.GrabAndScroll('.kt-datatable__table');

        // custom code end

        dataTable.on('click', 'tr', function () {
            const index = $('tr').index(this) - 1;
            if (dataTable.dataSet[index]) {
                setSelectedMember(dataTable.dataSet[index]);
                setRightSideDetails(true);
            }
        });
    }

    const closeDrawer = () => {
        Config.Core.CloseDrawer();
        setTimeout(() => {
            setRightSideDetails(false);
            setSelectedMember(null);
        }, 200);
    };

    return (
        <>
            <div className="kt-content kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor padding-tb0" id="kt_content">
                <div className="kt-container kt-container--fluid kt-grid__item kt-grid__item--fluid margin-t20">
                    <div className="kt-portlet kt-portlet--mobile">
                        <div className="kt-portlet__head kt-portlet__head--lg padding-l0">
                            <div className="kt-portlet__head-label" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <nav className="file-nav navbar navbar-top">
                                    <a
                                        className={navState === 'Golf Users' ? 'top-nav-active' : ''}
                                        onClick={() => handleNavChange('Golf Users')}
                                    >
                                        Golf Users
                                    </a>
                                    <a
                                        className={navState === 'Social Users' ? 'top-nav-active' : ''}
                                        onClick={() => handleNavChange('Social Users')}
                                    >
                                        Social Users
                                    </a>
                                </nav>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <label className="col-form-label text-right" style={{ whiteSpace: 'nowrap', paddingRight: '12px' }}>Status</label>
                                    <div style={{ width: '130px' }}>
                                        <Select
                                            id="ddlMemberStatus"
                                            inputId="ddlMemberStatus"
                                            className="multi-custom"
                                            value={{ value: statusFilter, label: statusFilter }}
                                            onChange={(opt) => setStatusFilter(opt.value)}
                                            options={[
                                                { value: 'All',     label: 'All'     },
                                                { value: 'Active',  label: 'Active'  },
                                                { value: 'Expired', label: 'Expired' },
                                            ]}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="kt-portlet__head-toolbar">
                                <div className="row align-items-center margin-r10">
                                    <div className="col-md-12 kt-margin-b-20-tablet-and-mobile margin-b0">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <div style={{ minWidth: '155px' }}>
                                                <Select
                                                    id="ddlMemberDateRange"
                                                    inputId="ddlMemberDateRange"
                                                    className="multi-custom"
                                                    placeholder="Date Range"
                                                    isClearable
                                                    value={dateRangeOptions.find(o => o.value === dateRange) || null}
                                                    onChange={(opt) => {
                                                        setDateRange(opt ? opt.value : null);
                                                        setFromDate('');
                                                        setToDate('');
                                                    }}
                                                    options={dateRangeOptions}
                                                />
                                            </div>
                                            <div className="kt-input-icon kt-input-icon--left">
                                                <input
                                                    type="text"
                                                    className="form-control clientSearch"
                                                    placeholder="Search..."
                                                    id="generalSearch"
                                                />
                                                <span className="kt-input-icon__icon kt-input-icon__icon--left">
                                                    <span><i className="la la-search"></i></span>
                                                </span>
                                            </div>
                                            <div className="dropdown">
                                                <button className="btn btn-label-brand dropdown-toggle" type="button" id="dropdownMemberExport" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                                    Export
                                                </button>
                                                <div className="dropdown-menu dropdown-menu-right" aria-labelledby="dropdownMemberExport">
                                                    <button type="button" className="dropdown-item" onClick={() => exportSheet('xlsx')}>Excel</button>
                                                    <button type="button" className="dropdown-item" onClick={() => exportSheet('csv')}>CSV</button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {dateRange === 'Custom' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 20px', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
                                <span style={{ fontSize: '0.85rem', color: '#6c757d', fontWeight: 500, whiteSpace: 'nowrap' }}>Custom Range:</span>
                                <div className="input-group date" style={{ width: '185px' }}>
                                    <input
                                        id="txtMemberFromDate"
                                        type="text"
                                        className="form-control date-input"
                                        placeholder="From Date"
                                        readOnly
                                    />
                                    <div className="input-group-append">
                                        <span className="input-group-text"><i className="la la-calendar"></i></span>
                                    </div>
                                </div>
                                <div className="input-group date" style={{ width: '185px' }}>
                                    <input
                                        id="txtMemberToDate"
                                        type="text"
                                        className="form-control date-input"
                                        placeholder="To Date"
                                        readOnly
                                    />
                                    <div className="input-group-append">
                                        <span className="input-group-text"><i className="la la-calendar"></i></span>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="kt-portlet__body kt-portlet__body--fit">
                            <div id="tblMembership" className="kt-datatable table-striped clickable"></div>
                            {rightSideDetails && selectedMember && (
                                <RightSideDetails title="Member Details" onCloseDrawer={closeDrawer}>

                                    {/* Header portlet(s) */}
                                    <div className="row">
                                        <div className={`col-${selectedMember.Category === 'Organization' ? '6' : '12'}`}>
                                            <div className="kt-portlet">
                                                <div className="kt-portlet__body">
                                                    <div className="kt-widget kt-widget--user-profile-3">
                                                        <div className="kt-widget__top">
                                                            {selectedMember.Photo && selectedMember.Photo !== '' ? (
                                                                <img className="kt-widget__pic kt-widget__pic" src={selectedMember.Photo} alt="user-profile" />
                                                            ) : (
                                                                <div className={`kt-widget__pic kt-widget__pic--${selectedMember.Category === 'Organization' ? 'success' : 'brand'} kt-font-${selectedMember.Category === 'Organization' ? 'success' : 'brand'} kt-font-boldest kt-hidden-`}>
                                                                    {selectedMember.FirstName.slice(0, 1) + selectedMember.LastName.slice(0, 1)}
                                                                </div>
                                                            )}
                                                            <div className="kt-widget__content">
                                                                <div className="kt-widget__head">
                                                                    <span className="kt-widget__username">
                                                                        {selectedMember.FirstName} {selectedMember.LastName}
                                                                        &nbsp;&nbsp;&nbsp;
                                                                        <span className={`kt-badge kt-badge--${selectedMember.MembershipType == 1 ? 'success' : 'info'} kt-badge--inline kt-badge--pill padding-lr10`}>
                                                                            {selectedMember.MembershipType == 1 ? 'Golf' : 'Social'}
                                                                        </span>
                                                                    </span>
                                                                </div>
                                                                <div className="kt-widget__subhead padding-t0">
                                                                    <span className="padding-r10">
                                                                        <i className="flaticon2-calendar-3"></i>
                                                                        {selectedMember.Category}
                                                                    </span>
                                                                    <span className="padding-r10">
                                                                        <i className="flaticon2-user"></i>
                                                                        {selectedMember.MembershipNo}
                                                                    </span>
                                                                    <br />
                                                                    {selectedMember.Mobile && (
                                                                        <span className="padding-r10">
                                                                            <i className="flaticon2-phone"></i>
                                                                            {selectedMember.Mobile}
                                                                        </span>
                                                                    )}
                                                                    {selectedMember.Email && (
                                                                        <span className="padding-r10">
                                                                            <i className="flaticon2-new-email"></i>
                                                                            {selectedMember.Email}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {selectedMember.Category === 'Organization' && (
                                            <div className="col-md-6">
                                                <div className="kt-portlet">
                                                    <div className="kt-portlet__body">
                                                        <div className="kt-widget kt-widget--user-profile-3">
                                                            <div className="kt-widget__top">
                                                                <div className="kt-widget__pic kt-widget__pic--success kt-font-success kt-font-boldest kt-hidden-">
                                                                    {selectedMember.CompanyName?.slice(0, 1)}
                                                                </div>
                                                                <div className="kt-widget__content">
                                                                    <div className="kt-widget__head">
                                                                        <span className="kt-widget__username">
                                                                            {selectedMember.CompanyName}
                                                                        </span>
                                                                    </div>
                                                                    <div className="kt-widget__subhead padding-t0">
                                                                        <span className="padding-r10">
                                                                            <i className="flaticon2-calendar-3"></i>
                                                                            {selectedMember.Category}
                                                                        </span>
                                                                        <br />
                                                                        {selectedMember.Email && (
                                                                            <span className="padding-r10">
                                                                                <i className="flaticon2-new-email"></i>
                                                                                {selectedMember.Email}
                                                                            </span>
                                                                        )}
                                                                        {selectedMember.Mobile && (
                                                                            <span className="padding-r10">
                                                                                <i className="flaticon2-phone"></i>
                                                                                {selectedMember.Mobile}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Subscription section */}
                                    <div className="kt-portlet kt-portlet--mobile">
                                        <div className="kt-portlet__head kt-portlet__head--lg padding-l0">
                                            <div className="kt-portlet__head-label">
                                                <nav className="file-nav navbar navbar-top">
                                                    <a
                                                        className={subNavState === 'Current Membership' ? 'top-nav-active' : ''}
                                                        onClick={() => setSubNavState('Current Membership')}
                                                    >
                                                        Current Membership
                                                    </a>
                                                    <a
                                                        className={subNavState === 'Upcoming' ? 'top-nav-active' : ''}
                                                        onClick={() => setSubNavState('Upcoming')}
                                                    >
                                                        Upcoming
                                                    </a>
                                                    <a
                                                        className={subNavState === 'Expired' ? 'top-nav-active' : ''}
                                                        onClick={() => setSubNavState('Expired')}
                                                    >
                                                        Expired
                                                    </a>
                                                </nav>
                                            </div>
                                            {(subNavState === 'Upcoming' || (subNavState === 'Current Membership' && membershipValidity.some(s => {
                                                if (s.Status !== 'Active') return false;
                                                const today = new Date(); today.setHours(0, 0, 0, 0);
                                                const expiry = new Date(s.SubscriptionExpiresOn + 'T00:00:00');
                                                return Math.round((expiry - today) / 86400000) + 1 <= 7;
                                            }))) && (
                                            <div className="kt-portlet__head-toolbar">
                                                <div className="kt-portlet__head-wrapper">
                                                    <div className="kt-portlet__head-actions">
                                                        <button
                                                            className="btn btn-label-brand btn-block margin-0"
                                                            onClick={(e) => { e.preventDefault(); setMembershipRecord({}); setShowMembershipModal(true); }}
                                                        >
                                                            <i className="la la-plus"></i>
                                                            Add New
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            )}
                                        </div>
                                        <div className="kt-portlet__body padding-t0">
                                            <div className="padding-lr10 padding-tb20">
                                                {membershipValidity.filter(s => s.Status === subStatusMap[subNavState]).length === 0 && (
                                                    <div style={{ textAlign: 'center' }} className="padding-t20">
                                                        <img src={subscription_expired} alt="membership-expired" height="255" width="350" style={{ opacity: '0.6' }} />
                                                        <h5>No {subNavState === 'Current Membership' ? 'Active' : subNavState} Membership</h5>
                                                    </div>
                                                )}
                                                {membershipValidity
                                                    .filter(s => s.Status === subStatusMap[subNavState])
                                                    .sort((a, b) => subNavState === 'Upcoming'
                                                        ? (new Date(a.SubscriptionStartsFrom) - new Date(b.SubscriptionStartsFrom))
                                                          || (new Date(a.SubscriptionExpiresOn) - new Date(b.SubscriptionExpiresOn))
                                                        : 0
                                                    )
                                                    .map((subscription, index) => {
                                                        const badgeClass = subscription.Status === 'Active'  ? 'success'
                                                                         : subscription.Status === 'Expired' ? 'grey'
                                                                         : 'brand';
                                                        const fontClass  = subscription.Status === 'Active'  ? 'success'
                                                                         : subscription.Status === 'Expired' ? 'danger'
                                                                         : 'brand';
                                                        return (
                                                            <div
                                                                key={index}
                                                                className="kt-portlet"
                                                                onClick={() => { setMembershipRecord(subscription); setShowMembershipModal(true); }}
                                                                style={{ boxShadow: '0px 0px 13px 0px rgb(82 63 105 / 25%)' }}
                                                                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0px 0px 20px 0px rgb(82 63 105 / 40%)'}
                                                                onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0px 0px 13px 0px rgb(82 63 105 / 25%)'}
                                                            >
                                                                <div className="kt-portlet__body">
                                                                    <div className="kt-section kt-section--space-md" style={{ marginBottom: '2px' }}>
                                                                        <div className="kt-widget24 kt-widget24--solid">
                                                                            <div className="kt-widget24__details">
                                                                                <div className="kt-widget24__info">
                                                                                    <a href="#" className="kt-widget24__title" title="Click to edit">
                                                                                        {subscription.SubscriptionName}
                                                                                        &nbsp;&nbsp;&nbsp;
                                                                                        <span className={`kt-badge kt-badge--${badgeClass} kt-badge--inline kt-badge--pill padding-lr10`}>
                                                                                            {subscription.Status}
                                                                                        </span>
                                                                                    </a>
                                                                                    <span className="kt-widget24__desc">
                                                                                        {subscription.Code}
                                                                                    </span><br />
                                                                                    <span className="kt-widget24__desc margin-tb5 padding-r100">
                                                                                        {subscription.Description}
                                                                                    </span>
                                                                                </div>
                                                                                <span className={`kt-widget24__stats kt-font-${fontClass} padding-l100`}>
                                                                                    ₹{subscription.Total}
                                                                                </span>
                                                                            </div>
                                                                            <div className="progress progress--sm">
                                                                                <div
                                                                                    className={`progress-bar kt-bg-${badgeClass}`}
                                                                                    role="progressbar"
                                                                                    style={{ width: subscription.SubscriptionExpiresOn && subscription.SubscriptionExpiresOn.startsWith('9999') ? '100%' : calculateProgress(subscription.SubscriptionStartsFrom, subscription.SubscriptionExpiresOn) }}
                                                                                    aria-valuenow="100" aria-valuemin="0" aria-valuemax="100"
                                                                                ></div>
                                                                            </div>
                                                                            <div className="kt-widget24__action">
                                                                                <span className="kt-widget24__change">
                                                                                    {moment(subscription.SubscriptionStartsFrom).format('DD-MMM-YYYY')}
                                                                                </span>
                                                                                <span className="kt-widget24__number">
                                                                                    {subscription.SubscriptionExpiresOn && subscription.SubscriptionExpiresOn.startsWith('9999') ? 'Lifetime' : moment(subscription.SubscriptionExpiresOn).format('DD-MMM-YYYY')}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                }
                                            </div>
                                        </div>
                                    </div>

                                </RightSideDetails>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {showMembershipModal && (
                <UpdateMembershipModal
                    show={showMembershipModal}
                    onDismissModal={setShowMembershipModal}
                    membershipRecord={membershipRecord}
                    setMembershipRecord={setMembershipRecord}
                    membershipValidity={membershipValidity}
                    userDetails={selectedMember}
                    from="#edit-user-modal"
                    allowEditStartDate={subNavState === 'Upcoming'}
                    vIndex={membershipValidity.length + 2}
                    refetchMembership={() => setSubRefresh((r) => !r)}
                />
            )}
        </>
    );
};

export default MembershipManagement;
